import { Injectable, inject } from '@angular/core';
import { FileViewer } from '@capacitor/file-viewer';
import { Directory, Filesystem, WriteFileResult } from '@capacitor/filesystem';
import { Share, ShareResult } from '@capacitor/share';
import { PlatformService } from './platform.service';
import { LoggingService } from './logging.service';

@Injectable({ providedIn: 'root' })
export class NativeDocumentService {
  private readonly platform = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);

  async saveDocument(blob: Blob, filename: string): Promise<string> {
    if (!this.platform.isNativeMobile()) {
      this.webDownload(blob, filename);
      return '';
    }

    const base64 = await this.blobToBase64(blob);

    if (this.platform.isIOS()) {
      const result = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache,
      });

      await Share.share({
        title: filename,
        url: result.uri,
        dialogTitle: 'Save Document',
      });

      return result.uri;
    } else {
      const result: WriteFileResult = await Filesystem.writeFile({
        path: `Download/${filename}`,
        data: base64,
        directory: Directory.ExternalStorage,
      });

      return result.uri;
    }
  }

  async saveToCache(blob: Blob, filename: string): Promise<string> {
    if (!this.platform.isNativeMobile()) {
      return URL.createObjectURL(blob);
    }

    const base64 = await this.blobToBase64(blob);

    const result = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });

    return result.uri;
  }

  async shareDocument(blob: Blob, filename: string, title?: string): Promise<ShareResult | void> {
    if (!this.platform.isNativeMobile()) {
      this.webDownload(blob, filename);
      return;
    }

    const uri = await this.saveToCache(blob, filename);

    return Share.share({
      title: title ?? 'Document',
      url: uri,
      dialogTitle: 'Share Document',
    });
  }

  async openDocument(blob: Blob, filename: string): Promise<string> {
    if (!this.platform.isNativeMobile()) {
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      return url;
    }

    const uri = await this.saveToCache(blob, filename);

    try {
      await FileViewer.openDocumentFromLocalPath({ path: uri });
    } catch {
      this.loggingService.logWarning(`NativeDocumentService: FileViewer failed for ${filename}, falling back to share`);
      await Share.share({
        title: filename,
        url: uri,
        dialogTitle: 'Open Document',
      });
    }

    return uri;
  }

  async deleteFile(path: string, directory: Directory = Directory.Documents): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }

    try {
      await Filesystem.deleteFile({ path, directory });
    } catch (error) {
      this.loggingService.logException(error, 'NativeDocumentService: Failed to delete file');
    }
  }

  async fileExists(path: string, directory: Directory = Directory.Documents): Promise<boolean> {
    if (!this.platform.isNativeMobile()) {
      return false;
    }

    try {
      await Filesystem.stat({ path, directory });
      return true;
    } catch {
      return false;
    }
  }

  async readFile(path: string, directory: Directory = Directory.Documents): Promise<string | null> {
    if (!this.platform.isNativeMobile()) {
      return null;
    }

    try {
      const result = await Filesystem.readFile({ path, directory });
      return result.data as string;
    } catch {
      return null;
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private webDownload(blob: Blob, filename: string): void {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }
}
